import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { getRecommendationSessionId, trackRecommendationEvent } from '../utils/recommendations';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';
import { useDepartmentSlug } from '../utils/departmentRouting';
import ProductPackagingPicker from '../components/products/ProductPackagingPicker';
import {
  defaultSelections,
  lbsToUnit,
  selectionsTotalLbs,
  type PackagingOption,
  type PackagingSelection,
} from '../utils/packagingMath';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type ProductDetails = {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  pricePerUnit: number;
  salePrice?: number | null;
  discountPercent?: number | null;
  baseUnit: 'LB' | 'KG';
  currentStockLbs?: number;
  isPreorderEnabled?: boolean;
  preorderEta?: string | null;
  category?: { id: string; name: string; slug: string } | null;
  packagingOptions?: PackagingOption[];
};

export default function ProductDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const departmentSlug = useDepartmentSlug();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addLine } = useCart();
  const [product, setProduct] = useState<ProductDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packagingSelections, setPackagingSelections] = useState<PackagingSelection[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/catalog/products/${id}`);
        if (departmentSlug) {
          url.searchParams.set('departmentSlug', departmentSlug);
        }
        const res = await fetch(
          applyContentLocaleToUrl(
            url.toString(),
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) throw new Error(t('storefront.product_load_error'));
        const data = (await res.json()) as ProductDetails;
        if (!cancelled) {
          setProduct(data);
          const opts = data.packagingOptions || [];
          setPackagingSelections(opts.length > 0 ? defaultSelections(opts, 1) : []);
          void trackRecommendationEvent({
            productId: data.id,
            eventType: 'PRODUCT_VIEW',
            context: 'product_details',
            source: 'product_page',
          });
          getRecommendationSessionId();
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('storefront.product_load_error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [departmentSlug, id, t, i18n.language, i18n.resolvedLanguage]);

  const basePriceUsd = product ? Number(product.salePrice ?? product.pricePerUnit) : 0;
  const discountPercent = Math.max(0, Math.min(100, Number(product?.discountPercent ?? 0)));
  const priceUsd =
    discountPercent > 1e-9 ? Math.round(basePriceUsd * (1 - discountPercent / 100) * 100) / 100 : basePriceUsd;
  const imageSrc =
    product?.imageUrl && product.imageUrl.startsWith('http')
      ? product.imageUrl
      : product?.imageUrl
        ? `${API_BASE}${product.imageUrl}`
        : undefined;

  const stockQty =
    product && typeof product.currentStockLbs === 'number'
      ? Math.max(0, product.currentStockLbs)
      : null;
  const canAddToCart = stockQty === null || stockQty > 0;
  const canPreorder = !canAddToCart && Boolean(product?.isPreorderEnabled);
  const packagingOptions = useMemo(
    () => product?.packagingOptions || [],
    [product?.packagingOptions],
  );
  const hasPackaging = packagingOptions.length > 0;
  const totalPackagedLbs = useMemo(
    () => selectionsTotalLbs(packagingSelections, packagingOptions),
    [packagingSelections, packagingOptions],
  );
  const canAddPackaged = hasPackaging ? totalPackagedLbs > 0 : true;
  const nextArrivalDateLabel =
    product?.preorderEta != null && String(product.preorderEta).trim().length > 0
      ? String(product.preorderEta).slice(0, 10)
      : null;

  return (
    <StorefrontLayout contentContained mainClassName="bg-transparent">
      <div>
        <h1 className="mb-6 text-lg font-semibold text-slate-900 sm:text-xl">
          {t('storefront.product_details')}
        </h1>

      <div className="w-full">
        {loading && <p className="text-sm text-slate-600">{t('common.loading')}</p>}
        {error && <p className="text-sm text-red-500">{error}</p>}

        {!loading && !error && product && (
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              {imageSrc ? (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img src={imageSrc} alt={product.name} className="h-80 w-full object-contain" />
              ) : (
                <div className="h-80 w-full rounded-xl bg-slate-200" />
              )}
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#00b853]">
                {product.category?.name ?? t('storefront.category')}
              </p>
              <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">
                {product.name}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {product.description || t('storefront.no_description')}
              </p>

              <div className="mt-5 flex items-center gap-3">
                {discountPercent > 1e-9 ? (
                  <div className="flex items-baseline gap-3">
                    <div className="text-sm font-semibold text-rose-600 line-through">
                      {formatPrice(basePriceUsd)}
                    </div>
                    <div className="text-xl font-extrabold text-[#00b853]">
                      {formatPrice(priceUsd)}
                    </div>
                  </div>
                ) : (
                  <div className="text-xl font-extrabold text-[#00b853]">
                    {formatPrice(priceUsd)}
                  </div>
                )}
                <div className="text-sm text-slate-500">
                  / {product.baseUnit.toLowerCase()}
                </div>
              </div>

              {stockQty !== null && (
                <div
                  className={`mt-4 inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                    canAddToCart
                      ? 'bg-slate-900/5 text-slate-700'
                      : 'bg-amber-100 text-amber-900'
                  }`}
                >
                  {canAddToCart
                    ? product.baseUnit === 'KG'
                      ? t('storefront.cart_stock_line_kg', { qty: stockQty.toFixed(1) })
                      : t('storefront.in_stock_lb', { qty: stockQty.toFixed(1) })
                    : t('storefront.out_of_stock')}
                </div>
              )}
              {canPreorder ? (
                <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                  <p className="font-semibold uppercase tracking-wide">
                    {t('storefront.preorder_available', { defaultValue: 'Pre-order available' })}
                  </p>
                  <p className="mt-1 text-indigo-800">
                    {nextArrivalDateLabel
                      ? t('storefront.preorder_next_arrival', {
                          defaultValue: 'Estimated Next arrival: {{date}}',
                          date: nextArrivalDateLabel,
                        })
                      : t('storefront.preorder_next_arrival_tbd', {
                          defaultValue: 'Next arrival date will be confirmed soon.',
                        })}
                  </p>
                </div>
              ) : null}

              {hasPackaging ? (
                <div className="mt-6">
                  <ProductPackagingPicker
                    options={packagingOptions}
                    unit={product.baseUnit}
                    selections={packagingSelections}
                    onChange={setPackagingSelections}
                    maxTotalLbs={canAddToCart ? stockQty : null}
                  />
                </div>
              ) : null}

              <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/cart')}
                  className="w-full rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 hover:border-[#00b853] hover:text-[#00b853] sm:w-auto"
                >
                  {t('storefront.go_to_cart')}
                </button>
                <button
                  type="button"
                  disabled={(!canAddToCart && !canPreorder) || !canAddPackaged}
                  onClick={() => {
                    if (!canAddToCart && !canPreorder) return;
                    if (hasPackaging && !canAddPackaged) return;
                    const qty = hasPackaging
                      ? lbsToUnit(totalPackagedLbs, product.baseUnit)
                      : 1;
                    addLine({
                      kind: 'product',
                      productId: product.id,
                      qty,
                      unit: product.baseUnit,
                      packagingSelections: hasPackaging ? packagingSelections : undefined,
                    });
                    void trackRecommendationEvent({
                      productId: product.id,
                      eventType: 'ADD_TO_CART',
                      context: 'product_details',
                      source: 'product_page',
                    });
                    navigate('/cart');
                  }}
                  aria-label={
                    canAddToCart
                      ? t('storefront.add_to_cart')
                      : canPreorder
                        ? t('storefront.preorder_now', { defaultValue: 'Pre-order now' })
                        : t('storefront.out_of_stock')
                  }
                  className={`w-full rounded-full px-5 py-2 text-sm font-semibold shadow-sm sm:w-auto ${
                    canAddToCart || canPreorder
                      ? 'bg-[#00b853] text-white hover:bg-[#009946]'
                      : 'cursor-not-allowed bg-slate-200 text-slate-500'
                  }`}
                >
                  {canAddToCart
                    ? t('storefront.add_to_cart')
                    : canPreorder
                      ? t('storefront.preorder_now', { defaultValue: 'Pre-order now' })
                      : t('storefront.out_of_stock')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </StorefrontLayout>
  );
}

